import { useEffect } from "react";
import emailjs from "emailjs-com";

const SESSION_KEY = "nirkana_visit_logged";

/**
 * Sends a one-time-per-browser-session notification email when someone
 * lands on the site, via the same EmailJS account already used for the
 * quote/contact/vendor forms — no separate backend required.
 *
 * Limits (real, not hidden): this only fires if JavaScript runs and the
 * request isn't blocked by an ad blocker, tracker blocker, or browser
 * privacy mode — a meaningful share of visitors will block this kind of
 * client-side beacon. It's a best-effort "someone's on the site" ping,
 * not reliable analytics. For real traffic numbers, pair this with
 * Google Analytics or a server-side log.
 */
const useVisitNotifier = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(SESSION_KEY)) return;

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_VISIT_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) return;

    const send = (locationInfo) => {
      emailjs
        .send(serviceId, templateId, {
          page: window.location.href,
          referrer: document.referrer || "Direct / unknown",
          time: new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }),
          location: locationInfo,
        }, publicKey)
        .then(() => sessionStorage.setItem(SESSION_KEY, "1"))
        .catch(() => {
          /* fail silently — a missed visit notification shouldn't affect the visitor */
        });
    };

    // Best-effort approximate location from IP; falls back gracefully if blocked/unavailable.
    fetch("https://ipapi.co/json/")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const locationInfo = data
          ? [data.city, data.region, data.country_name].filter(Boolean).join(", ")
          : "Unavailable";
        send(locationInfo);
      })
      .catch(() => send("Unavailable"));
  }, []);
};

export default useVisitNotifier;
